import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from 'react-query'
import { api } from '../services/api'
import { CheckCircle, ArrowRight, BarChart3 } from 'lucide-react'

const Onboarding = () => {
  const [step, setStep] = useState(1)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const navigate = useNavigate()

  const onboardingMutation = useMutation(
    async (data: { platforms: string[] }) => {
      const response = await api.post('/api/auth/onboarding', data)
      return response.data
    },
    {
      onSuccess: () => {
        // Onboarding completed
        navigate('/dashboard')
      },
      onError: (error) => {
        console.error('Onboarding error:', error)
        // Continue anyway since this is just platform selection
        navigate('/dashboard')
      }
    }
  )

  const platforms = [
    { id: 'etsy', name: 'Etsy', description: 'Sync your Etsy shop sales and revenue' },
    { id: 'amazon', name: 'Amazon', description: 'Track Amazon Seller Central orders and profits' },
    { id: 'tpt', name: 'Teachers Pay Teachers', description: 'Monitor TPT sales and earnings' },
    { id: 'printify', name: 'Printify', description: 'Track product costs and inventory' }
  ]

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    )
  }

  const handleContinue = () => {
    if (step === 1) {
      setStep(2)
    } else if (step === 2) {
      onboardingMutation.mutate({ platforms: selectedPlatforms })
    }
  }

  const renderStep1 = () => (
    <div className="text-center space-y-6">
      <div className="flex justify-center">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
          <BarChart3 className="w-8 h-8 text-white" />
        </div>
      </div>
              <h2 className="text-3xl font-bold text-gray-900">Welcome to PlatformRadar!</h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Let's set up your revenue intelligence platform. We'll help you detect revenue opportunities across all your sales platforms.
      </p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
        <p className="text-blue-800 text-sm">
          <strong>What's next:</strong> Choose which platforms you sell on, and we'll configure your revenue tracking automatically.
        </p>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Select Your Platforms</h2>
        <p className="text-gray-600 mt-2">
          Choose the platforms where you sell your products. We'll help you track revenue across all of them.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {platforms.map((platform) => (
          <div
            key={platform.id}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedPlatforms.includes(platform.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handlePlatformToggle(platform.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlatforms.includes(platform.id)
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {selectedPlatforms.includes(platform.id) && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{platform.name}</h3>
                  <p className="text-sm text-gray-600">{platform.description}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {selectedPlatforms.length > 0 && (
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Selected: {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  )

  const renderStep3 = () => (
    <div className="text-center space-y-6">
      <div className="flex justify-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
      </div>
      <h2 className="text-3xl font-bold text-gray-900">You're All Set!</h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Your PlatformRadar account is ready. We'll help you detect revenue opportunities and track your business growth across all platforms.
      </p>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
        <p className="text-green-800 text-sm">
          <strong>Next:</strong> Configure your platform integrations in Settings to start tracking revenue.
        </p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNumber
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step > stepNumber ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation */}
          <div className="mt-8 flex justify-center">
            {step < 3 && (
              <button
                onClick={handleContinue}
                disabled={step === 2 && selectedPlatforms.length === 0}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {onboardingMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Setting up your account...
                  </>
                ) : (
                  <>
                    {step === 1 ? 'Get Started' : 'Continue'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Onboarding 